import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import axios from "axios";
import EmptySection from "../EmptyStates/EmptySection";
import ftuImgExercises from "../../resources/ftu-exercises.png";
import FilterMenu from "../Schedule/FilterMenu";
import NoSearchResults from "../EmptyStates/NoSearchResults";
import useFetchMuscleGroupsList from "../../hooks/FetchMuscleGroupsList";
import useFetchFitnessComponentsList from "../../hooks/FetchFitnessComponentsList";
import useFetchExerciseFilters from "../../hooks/exercises/FetchExerciseFilters";
import ExerciseListSkeleton from "../LoadingStates/ExerciseListSkeleton";
import nasmApi from "../../api/endpoints";
import BodyMuscleSelection from "../Exercises/BodyMuscleSelection";
import { useLocation } from "react-router-dom";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ClearOptions, HighlightSearchBar } from "../Inputs";
import { useExercisesContext } from "../../contexts/ExercisesContext";
import { usePaginationObserver, useDebounce } from "../../util/utilFunctions";
import { SortButton } from "../Buttons";
import SelectedFilterButton from "../Buttons/SelectedFilterButton";
import { textStrings } from "../../textStrings";
import SupersetCircuitList from "./SupersetCircuitList";

const SupetsetCircuitListRef = React.forwardRef((props, ref) => {
  return <SupersetCircuitList lastExerciseRecordRef={ref} {...props} />;
});

const useStyles = makeStyles({
  boxPadding: {
    paddingRight: "20px",
  },
});

const SETS_TAB = Object.freeze({
  SUPERSETS: "supersets",
  CIRCUITS: "circuits",
  FAVORITES: "favorites",
});

const EXCLUDED_MUSCLES = [
  "arms",
  "legs",
  "upper body",
  "lower body",
  "total body",
];

function SearchBar(props) {
  const { clearSearchText, searchText, handleSearch } = props;
  const classes = useStyles();
  return (
    <Box className={classes.boxPadding}>
      <HighlightSearchBar
        isClearable={searchText.length > 0}
        onClickClear={clearSearchText}
        value={searchText}
        onChange={handleSearch}
      />
    </Box>
  );
}

function SuperSetCircuitListContent(props) {
  const {
    areSearchResultsNotFound,
    isInitialListLoading,
    isFirstTimeUserExp,
    exerciseData,
    superSets,
    currentTab,
    lastExerciseRecordRef,
    isLoading,
    selectedExerciseId,
    setSelectedExerciseId,
    viewExerciseDetailsLink,
    viewSuperSetsLink,
    setSuperSetData,
    setSupersetPageNumber,
    hasMoreSuperset,
    isSuperSetLoading,
    viewCircuitLink,
    setCircuitsData,
    setIsExerciseDataRefresh,
  } = props;

  if (isFirstTimeUserExp) {
    return (
      <EmptySection
        ftuImg={ftuImgExercises}
        emptyTitle="Upload your own Exercises"
        emptyDescription="Make your workouts more personable by creating your own exercises."
        ftuBtnHidden
      />
    );
  }

  if (isInitialListLoading) {
    return <ExerciseListSkeleton />;
  }

  if (areSearchResultsNotFound) {
    return <NoSearchResults />;
  }

  return (
    <SupetsetCircuitListRef
      exerciseData={exerciseData}
      superSets={superSets}
      currentTab={currentTab}
      lastExerciseRecordRef={lastExerciseRecordRef}
      isLoading={isLoading}
      selectedExerciseId={selectedExerciseId}
      setSelectedExerciseId={setSelectedExerciseId}
      viewExerciseDetailsLink={viewExerciseDetailsLink}
      viewSuperSetsLink={viewSuperSetsLink}
      setSuperSetData={setSuperSetData}
      setSupersetPageNumber={setSupersetPageNumber}
      hasMoreSuperset={hasMoreSuperset}
      isSuperSetLoading={isSuperSetLoading}
      viewCircuitLink={viewCircuitLink}
      setCircuitsData={setCircuitsData}
      setIsExerciseDataRefresh={setIsExerciseDataRefresh}
    />
  );
}

const useSearchAndFiltersStyle = makeStyles({
  boxContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "20px",
  },
  selectedFilterItems: {
    marginLeft: "12px",
    display: "inline-flex",
    flexWrap: "wrap",
  },
});

/**
 * @return {JSX|null}
 */
const SearchAndFilters = (props) => {
  const {
    visible,
    clearSearchText,
    searchText,
    handleSearch,
    myExercises,
    exerciseSources,
    muscleGroups,
    fitnessComponents,
    pendingSourceFilters,
    pendingMuscleGroupFilters,
    pendingFitnessComponentFilters,
    handleFilters,
    setPendingSourceFilters,
    setPendingMuscleGroupFilters,
    setPendingFitnessComponentFilters,
    clearAllFilters,
    activeFilterCount,
    onCloseFilter,
    isSortActive,
    onToggleSort,
    isClearOptionsVisible,
    onClearOptions,
    sourceFilters,
    muscleGroupFilters,
    fitnessComponentFilters,
    onRemoveSourceFilter,
    onRemoveMuscleGroupFilter,
    onRemoveFitnessFilter,
  } = props;

  const classes = useSearchAndFiltersStyle();
  if (!visible) {
    return null;
  }
  const selectedSources = exerciseSources.filter((item) =>
    sourceFilters.includes(item.filter_value),
  );
  const selectedMuscleGroup = muscleGroups.filter((item) =>
    muscleGroupFilters.includes(item.id),
  );
  const selectedFitnessComponents = fitnessComponents.filter((item) =>
    fitnessComponentFilters.includes(item.id),
  );

  return (
    <Box className={classes.boxContainer}>
      <SearchBar
        clearSearchText={clearSearchText}
        searchText={searchText}
        handleSearch={handleSearch}
      />
      <FilterMenu
        fromExercise={true}
        sources={myExercises ? [] : exerciseSources}
        muscleGroups={muscleGroups}
        fitnessComponents={fitnessComponents}
        // Current active filters
        sourceFilters={pendingSourceFilters}
        muscleGroupFilters={pendingMuscleGroupFilters}
        fitnessComponentFilters={pendingFitnessComponentFilters}
        handleSourceFilters={(source) =>
          handleFilters(source, setPendingSourceFilters)
        }
        handleMuscleGroupFilters={(muscle) =>
          handleFilters(muscle, setPendingMuscleGroupFilters)
        }
        handleFitnessComponentFilters={(component) =>
          handleFilters(component, setPendingFitnessComponentFilters)
        }
        clearAllFilters={clearAllFilters}
        activeFilterCount={activeFilterCount}
        onClose={onCloseFilter}
      />
      <SortButton
        text="Most Recent"
        margin="0 0 0 12px"
        isActive={isSortActive}
        onClick={onToggleSort}
      />
      <Box className={classes.selectedFilterItems}>
        {myExercises
          ? null
          : selectedSources.map((i) => (
              <SelectedFilterButton
                margin="0 0 0 12px"
                onRemove={() => onRemoveSourceFilter(i?.filter_value)}
                key={i?.filter_value}
              >
                {i?.display_value}
              </SelectedFilterButton>
            ))}
        {selectedMuscleGroup.map((i) => (
          <SelectedFilterButton
            key={i?.id}
            margin="0 0 0 12px"
            onRemove={() => onRemoveMuscleGroupFilter(i?.id)}
          >
            {i?.name}
          </SelectedFilterButton>
        ))}
        {selectedFitnessComponents.map((i) => (
          <SelectedFilterButton
            key={i?.id}
            margin="0 0 0 12px"
            onRemove={() => onRemoveFitnessFilter(i?.id)}
          >
            {i?.name}
          </SelectedFilterButton>
        ))}
      </Box>
      <ClearOptions
        text="Clear All"
        visible={isClearOptionsVisible}
        margin="0 0 0 12px"
        onClick={onClearOptions}
      />
    </Box>
  );
};

function SupersetCircuitScreen(props) {
  const {
    selectedExerciseId,
    setSelectedExerciseId,
    resetSelection,
    viewExerciseDetailsLink,
    viewSuperSetsLink,
    setSuperSetData,
    viewCircuitLink,
    setCircuitsData,
  } = props;

  const [searchText, setSearchText] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  // Delay search until user is done typing in search bar
  const debouncedSearch = useDebounce(searchText, 500);

  const location = useLocation();
  const currentTab = location.pathname.split("/").pop();

  const { exerciseFilters } = useFetchExerciseFilters();
  const { muscleGroups: bodyRegionFilters } = useFetchMuscleGroupsList();
  const muscleGroups = [{ id: null, name: "Bodymap" }, ...bodyRegionFilters];
  const { fitnessComponents } = useFetchFitnessComponentsList();
  const exerciseSources = exerciseFilters.filter(
    (src) => !["nasm", "trainer"].includes(src.filter_value),
  );
  const [sourceFilters, setSourceFilters] = useState([]);
  const [muscleGroupFilters, setMuscleGroupFilters] = useState([]);
  const [fitnessComponentFilters, setFitnessComponentFilters] = useState([]);

  const [pendingSourceFilters, setPendingSourceFilters] = useState([]);
  const [pendingMuscleGroupFilters, setPendingMuscleGroupFilters] = useState(
    [],
  );
  const [
    pendingFitnessComponentFilters,
    setPendingFitnessComponentFilters,
  ] = useState([]);

  const [isSortActive, setSortActive] = useState(false);
  const [isClearOptionsVisible, setClearOptionsVisible] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [isFirstTimeUserExp, setFirstTimeUserExp] = useState(false);

  const tempMuscleGrpIds = useMemo(() => muscleGroupFilters.join(","), [
    muscleGroupFilters,
  ]);
  const tempFitnessComponentIds = useMemo(
    () => fitnessComponentFilters.join(","),
    [fitnessComponentFilters],
  );

  const [showBodyMap, setShowBodyMap] = useState(false);
  const [selectedMuscles, setSelectedMuscles] = useState([]);
  const [supersetPageNumber, setSupersetPageNumber] = useState(1);
  const [hasMoreSuperset, setHasMoreSuperset] = useState(true);
  const [isSuperSetLoading, setIsSuperSetLoading] = useState(false);
  const [compoundRoutines, setCompoundRoutines] = useState([]);
  const [filterOpen, setFilterOpen] = useState({
    event: null,
    open: false,
  });
  const [isExerciseDataRefresh, setIsExerciseDataRefresh] = useState(false);

  useEffect(() => {
    fetchAllSuperSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentTab, supersetPageNumber]);

  const fetchAllSuperSet = async () => {
    
      const jsonData = {
        page: supersetPageNumber,
        size: 20,
        search: debouncedSearch,
      };
      if (hasMoreSuperset) {
        setIsSuperSetLoading(true);
        try {
          const res = await nasmApi.supersets.fetchAllSuperSets(jsonData);
          if (res) {
            setHasMoreSuperset(res?.result?.hasMore);
            setCompoundRoutines((prevState) =>
              jsonData.page === 1
                ? [...res?.result?.compoundRoutines]
                : [...prevState, ...res?.result?.compoundRoutines],
            );
            setIsSuperSetLoading(false);
            setIsExerciseDataRefresh(false);
          }
        } catch (error) {
          alert(error?.message || textStrings.somethingWentWrong);
        }
      }
    
  };
    
let activeExerciseFilter = "";
  if (currentTab === SETS_TAB.CIRCUITS) {
    activeExerciseFilter =
      sourceFilters.length > 0 ? sourceFilters.join(",") : "nasm";
  } else if (currentTab === SETS_TAB.FAVORITES) {
    activeExerciseFilter =
      sourceFilters.length > 0
        ? `${sourceFilters.join(",")},favorites`
        : "favorites";
  } else if (currentTab === SETS_TAB.SUPERSETS) {
    activeExerciseFilter = "trainer";
  }

  const {
    fetchExercises,
    clearExercises,
    exercises: exerciseData,
    loading,
    hasMore,
    pageNumber: newPageNumber,
  } = useExercisesContext();

  // Reset Search and Filters when Tab changes
  useEffect(() => {
    clearAllFilters();
    setSearchText("");
    setSortActive(false);
  }, [currentTab]);

  useEffect(() => {
    setPageNumber(newPageNumber);
  }, [newPageNumber]);

  // Reset exercise list when any
  // search/filtering/sorting fields change
  useEffect(() => {
    clearExercises();
    setPageNumber(1);
  }, [
    clearExercises,
    debouncedSearch,
    activeExerciseFilter,
    tempMuscleGrpIds,
    tempFitnessComponentIds,
    sortField,
    sortOrder,
    isExerciseDataRefresh,
  ]);

  useEffect(() => {
    setPageNumber(1);
    if (isSortActive) {
      setSortField("created_at");
      setSortOrder("desc");
    } else {
      setSortField("");
      setSortOrder("");
    }
  }, [isSortActive]);

  // Used to determine whether or not FTU Panel should be shown
  useEffect(() => {
    const cancel = axios.CancelToken.source();
    if (currentTab === SETS_TAB.SUPERSETS) {
      const searchParams = {
        page: 1,
        size: 1,
        exercisesFilter: "trainer",
      };
      nasmApi.nasmExercises
        .findExercises(searchParams, cancel.token)
        .then((data) => {
          if (!data.isRequestCancelled) {
            if (!!data.result) {
              setFirstTimeUserExp(
                data.result.exercises.length === 0 &&
                  currentTab === SETS_TAB.SUPERSETS,
              );
            } else {
              setFirstTimeUserExp(false);
            }
          }
        });
    } else {
      setFirstTimeUserExp(false);
    }

    return () => cancel.cancel("Exercise Search Request FTU Cancelled");
  }, [currentTab]);

  const rootElement = useRef();

  const lastExerciseRecordRef = usePaginationObserver({
    setPageNumber: setPageNumber,
    isLoading: loading,
    hasMoreDataToLoad: hasMore,
    rootElement: rootElement.current,
  });
  useEffect(() => {
    if (
      currentTab === SETS_TAB.CIRCUITS ||
      currentTab === SETS_TAB.SUPERSETS ||
      currentTab === SETS_TAB.FAVORITES ||
      !hasMoreSuperset ||
      debouncedSearch.length > 0
    ) {
      fetchExercises({
        pageNumber,
        searchText: debouncedSearch,
        sizePerPage: 20,
        activeExerciseFilter,
        muscleGroupIds: tempMuscleGrpIds,
        fitnessComponentIds: tempFitnessComponentIds,
        sortField: sortField,
        sortOrder: sortOrder,
      });
      setIsExerciseDataRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTab,
    hasMoreSuperset,
    fetchExercises,
    pageNumber,
    debouncedSearch,
    activeExerciseFilter,
    muscleGroupFilters,
    fitnessComponentFilters,
    sortField,
    sortOrder,
    resetSelection,
    isExerciseDataRefresh,
  ]);

  const updateMuscles = useCallback(() => {
    const musclesCopy = [];
    pendingMuscleGroupFilters.forEach((filterId) => {
      const filter = bodyRegionFilters.find((fltr) => fltr.id === filterId);
      if (filter && !EXCLUDED_MUSCLES.includes(filter.name?.toLowerCase())) {
        musclesCopy.push({
          muscles: [filter.name?.toLowerCase().replace(" ", "")],
          frequency: 1,
        });
      }
    });
    setSelectedMuscles(musclesCopy);
  }, [pendingMuscleGroupFilters, setSelectedMuscles, bodyRegionFilters]);

  useEffect(() => {
    updateMuscles();
  }, [updateMuscles]);

  function handleSearch(e) {
    setSearchText(e.target.value);
    setPageNumber(1);
    resetSelection();
    setSupersetPageNumber(1);
    setHasMoreSuperset(true);
    setCompoundRoutines([]);
  }

  const handleFilters = (id, updateActiveFilters) => {
    if (id) {
      updateActiveFilters((prevState) => {
        const filtersCopy = [...prevState];
        const idIndex = filtersCopy.indexOf(id);
        if (idIndex !== -1) {
          filtersCopy.splice(idIndex, 1);
        } else {
          filtersCopy.push(id);
        }
        return [...new Set([...filtersCopy])];
      });
    } else {
      setShowBodyMap(!showBodyMap);
      updateMuscles();
    }
  };

  const clearAllFilters = () => {
    setSourceFilters([]);
    setMuscleGroupFilters([]);
    setFitnessComponentFilters([]);
    setPendingSourceFilters([]);
    setPendingMuscleGroupFilters([]);
    setPendingFitnessComponentFilters([]);
    setPageNumber(1);
  };

  const clearSearchText = () => {
    setSearchText("");
    setPageNumber(1);
    setSupersetPageNumber(1);
    setHasMoreSuperset(true);
    setCompoundRoutines([]);
  };

  const onCloseFilter = () => {
    setSourceFilters(pendingSourceFilters);
    setFitnessComponentFilters(pendingFitnessComponentFilters);
    setMuscleGroupFilters(pendingMuscleGroupFilters);
    setPageNumber(1);
    setClearOptionsVisible(true);
    setFilterOpen({ event: null, open: false });
    setIsExerciseDataRefresh(true);
  };

  const onClearOptions = () => {
    setSortActive(false);
    setClearOptionsVisible(false);

    clearAllFilters();
  };

  const onToggleSort = () => {
    setSortActive((prev) => {
      if (
        prev &&
        !fitnessComponentFilters.length &&
        !muscleGroupFilters.length &&
        !sourceFilters.length
      ) {
        setClearOptionsVisible(false);
      } else {
        setClearOptionsVisible(true);
      }
      return !prev;
    });
  };

  const activeFilterCount =
    sourceFilters.length +
    muscleGroupFilters.length +
    fitnessComponentFilters.length;

  const areSearchResultsNotFound =
    !loading &&
    exerciseData.length === 0 &&
    compoundRoutines.length === 0 &&
    (searchText !== "" || activeFilterCount >= 1);
  const isInitialListLoading =
    (isSuperSetLoading &&
      compoundRoutines.length === 0 &&
      currentTab === SETS_TAB.SUPERSETS) ||
    (loading && exerciseData.length === 0 && currentTab === SETS_TAB.CURCUITS);

  const onRemoveSourceFilter = (i) => {
    const filters = sourceFilters.filter((selected) => selected !== i);
    setSourceFilters(filters);
    setPendingSourceFilters(filters);
  };

  const onRemoveMuscleGroupFilter = (id) => {
    const filters = muscleGroupFilters.filter(
      (selectedId) => selectedId !== id,
    );
    setMuscleGroupFilters(filters);
    setPendingMuscleGroupFilters(filters);
  };

  const onRemoveFitnessFilter = (id) => {
    const filters = fitnessComponentFilters.filter(
      (selectedId) => selectedId !== id,
    );
    setFitnessComponentFilters(filters);
    setPendingFitnessComponentFilters(filters);
  };

  return (
    <Box ref={rootElement} style={{ height: "inherit" }}>
      <SearchAndFilters
        visible={!isFirstTimeUserExp}
        clearSearchText={clearSearchText}
        searchText={searchText}
        handleSearch={handleSearch}
        myExercises={true}
        exerciseSources={exerciseSources}
        muscleGroups={muscleGroups}
        fitnessComponents={fitnessComponents}
        pendingSourceFilters={pendingSourceFilters}
        pendingMuscleGroupFilters={pendingMuscleGroupFilters}
        pendingFitnessComponentFilters={pendingFitnessComponentFilters}
        handleFilters={handleFilters}
        setPendingSourceFilters={setPendingSourceFilters}
        setPendingMuscleGroupFilters={setPendingMuscleGroupFilters}
        setPendingFitnessComponentFilters={setPendingFitnessComponentFilters}
        clearAllFilters={clearAllFilters}
        activeFilterCount={activeFilterCount}
        onCloseFilter={onCloseFilter}
        isSortActive={isSortActive}
        onToggleSort={onToggleSort}
        isClearOptionsVisible={isClearOptionsVisible}
        onClearOptions={onClearOptions}
        sourceFilters={sourceFilters}
        muscleGroupFilters={muscleGroupFilters}
        fitnessComponentFilters={fitnessComponentFilters}
        onRemoveSourceFilter={onRemoveSourceFilter}
        onRemoveMuscleGroupFilter={onRemoveMuscleGroupFilter}
        onRemoveFitnessFilter={onRemoveFitnessFilter}
        filterOpen={filterOpen}
      />
      <SuperSetCircuitListContent
        areSearchResultsNotFound={areSearchResultsNotFound}
        isInitialListLoading={isInitialListLoading}
        isFirstTimeUserExp={isFirstTimeUserExp}
        exerciseData={exerciseData}
        superSets={compoundRoutines}
        currentTab={currentTab}
        lastExerciseRecordRef={lastExerciseRecordRef}
        isLoading={loading}
        selectedExerciseId={selectedExerciseId}
        setSelectedExerciseId={setSelectedExerciseId}
        viewExerciseDetailsLink={viewExerciseDetailsLink}
        viewSuperSetsLink={viewSuperSetsLink}
        setSuperSetData={setSuperSetData}
        setSupersetPageNumber={setSupersetPageNumber}
        hasMoreSuperset={hasMoreSuperset}
        isSuperSetLoading={isSuperSetLoading}
        viewCircuitLink={viewCircuitLink}
        setCircuitsData={setCircuitsData}
        setIsExerciseDataRefresh={setIsExerciseDataRefresh}
      />
      {showBodyMap ? (
        <BodyMuscleSelection
          bodyRegionFilters={bodyRegionFilters}
          selectedMuscles={selectedMuscles}
          setSelectedMuscles={setSelectedMuscles}
          showBodyMap={showBodyMap}
          setShowBodyMap={setShowBodyMap}
          setPendingMuscleGroupFilters={setPendingMuscleGroupFilters}
        />
      ) : null}
    </Box>
  );
}

SupersetCircuitScreen.propTypes = {
  selectedExerciseId: PropTypes.number.isRequired,
  setSelectedExerciseId: PropTypes.func.isRequired,
  resetSelection: PropTypes.func.isRequired,
  viewExerciseDetailsLink: PropTypes.func.isRequired,
};

export default SupersetCircuitScreen;
